/**
 * ChainContext
 * Manages selected EVM chain state for multi-chain bridge.
 *
 * Hooks (useChainContext, useSelectedChain, useChainSelection) are in useChain.ts
 * to satisfy react-refresh/only-export-components — a file with a provider component
 * must not also export hooks.
 */

import { useState, useEffect, useCallback, type ReactNode } from 'react';

import { useChains } from 'hooks/api/useChains';
import {
  DEFAULT_CHAIN_ID,
  saveSelectedChain,
  loadSelectedChain,
} from 'config';
import type { Chain } from 'types';

import { ChainContext } from './chainContextDef';
import type { ChainContextType } from './chainContextDef';

export type { ChainContextType } from './chainContextDef';

interface ChainProviderProps {
  children: ReactNode;
}

/**
 * Provider component for chain selection state
 */
export function ChainProvider({ children }: ChainProviderProps) {
  // Fetch chains from API
  const {
    chains,
    enabledChains,
    isLoading: isLoadingChains,
    isError,
    error,
    getChainById,
    getChainByName,
  } = useChains();

  // Selected chain state
  const [selectedChain, setSelectedChainState] = useState<Chain | null>(null);

  // Initialize selected chain from localStorage or default
  useEffect(() => {
    if (enabledChains.length > 0 && !selectedChain) {
      // Try to restore from localStorage
      const savedChainId = loadSelectedChain();

      if (savedChainId) {
        const savedChain = enabledChains.find(c => c.chainId === savedChainId);
        if (savedChain) {
          setSelectedChainState(savedChain);

          return;
        }
      }

      // Fall back to default chain if available
      const defaultChain = enabledChains.find(c => c.chainId === DEFAULT_CHAIN_ID);
      if (defaultChain) {
        setSelectedChainState(defaultChain);
      } else {
        // Use first enabled chain as fallback
        setSelectedChainState(enabledChains[0]);
      }
    }
  }, [enabledChains, selectedChain]);

  // Re-sync the selected chain object when fresher chain data arrives.
  // On first paint the selection comes from the static fallback list
  // (e.g. shortName "Sepolia", possibly stale lockerAddress); once the API
  // responds with canonical data ("Ethereum Sepolia", live locker address),
  // swap in the matching object so the UI and tx params use API truth.
  useEffect(() => {
    if (!selectedChain) {
      return;
    }

    const fresh = enabledChains.find(c => c.chainId === selectedChain.chainId);
    // Compare by value, not reference: the static fallback list is rebuilt
    // every render, so a reference check would re-set state in a loop.
    // confirmations/blockTime feed the finality estimate — the static fallback
    // hardcodes blockTime 12 for every chain, so omitting them here left Base
    // Sepolia (2s blocks) quoting Ethereum's ~15 min instead of ~4 min.
    if (
      fresh &&
      (fresh.shortName !== selectedChain.shortName ||
        fresh.lockerAddress !== selectedChain.lockerAddress ||
        fresh.explorerUrl !== selectedChain.explorerUrl ||
        fresh.confirmations !== selectedChain.confirmations ||
        fresh.blockTime !== selectedChain.blockTime ||
        fresh.isEnabled !== selectedChain.isEnabled)
    ) {
      setSelectedChainState(fresh);
    }
  }, [enabledChains, selectedChain]);

  // Set selected chain and persist to localStorage
  const setSelectedChain = useCallback((chain: Chain) => {
    setSelectedChainState(chain);
    saveSelectedChain(chain.chainId);
  }, []);

  // Set selected chain by ID
  const setSelectedChainById = useCallback((chainId: number) => {
    const chain = enabledChains.find(c => c.chainId === chainId);
    if (chain) {
      setSelectedChain(chain);
    }
  }, [enabledChains, setSelectedChain]);

  // Get locker address for selected chain
  const getLockerAddress = useCallback((): string | undefined => {
    return selectedChain?.lockerAddress;
  }, [selectedChain]);

  // Get explorer URL for selected chain
  const getExplorerUrl = useCallback((): string | undefined => {
    return selectedChain?.explorerUrl;
  }, [selectedChain]);

  // Get explorer URL for a transaction
  const getExplorerTxUrl = useCallback((txHash: string): string => {
    if (!selectedChain?.explorerUrl) {
      return `#tx/${txHash}`;
    }

    return `${selectedChain.explorerUrl}/tx/${txHash}`;
  }, [selectedChain]);

  // Get explorer URL for an address
  const getExplorerAddressUrl = useCallback((address: string): string => {
    if (!selectedChain?.explorerUrl) {
      return `#address/${address}`;
    }

    return `${selectedChain.explorerUrl}/address/${address}`;
  }, [selectedChain]);

  const value: ChainContextType = {
    // Chain data
    chains,
    enabledChains,
    isLoadingChains,
    chainsError: isError ? (error as Error) : null,

    // Selected chain
    selectedChain,
    selectedChainId: selectedChain?.chainId ?? null,
    setSelectedChain,
    setSelectedChainById,

    // Helpers
    getChainById,
    getChainByName,
    getLockerAddress,
    getExplorerUrl,
    getExplorerTxUrl,
    getExplorerAddressUrl,

    // Status
    isChainSelected: selectedChain !== null,
    isSelectedChainEnabled: selectedChain?.isEnabled ?? false,
  };

  return (
    <ChainContext.Provider value={value}>
      {children}
    </ChainContext.Provider>
  );
}
